import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { signInWithPopup, signInWithRedirect, getRedirectResult } from "firebase/auth";
import { auth, googleProvider } from "../../firebase";
import { useAuth } from "../../AuthContext";
import { apiFetch } from "../../api";
import './Invite.sass';

const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
            <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
    );
}

export default function Invite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [invite, setInvite] = useState(null);
    const [pageError, setPageError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [actionError, setActionError] = useState(null);

    useEffect(() => {
        fetch(`/api/invite/${token}`)
            .then(r => r.json())
            .then(data => {
                if (data.error) setPageError(data.error);
                else setInvite(data);
            })
            .catch(() => setPageError("Kunde inte hämta inbjudan."))
            .finally(() => setLoading(false));
    }, [token]);

    // Handle redirect result on iOS after sign-in redirect
    useEffect(() => {
        if (!isIOS) return;
        setAccepting(true);
        getRedirectResult(auth)
            .then(result => {
                if (result?.user) return callAccept();
            })
            .catch(err => {
                if (err.code !== 'auth/cancelled-popup-request') {
                    setActionError("Inloggning misslyckades. Försök igen.");
                }
            })
            .finally(() => setAccepting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const callAccept = async () => {
        setAccepting(true);
        setActionError(null);
        try {
            const res = await apiFetch(`/invite/${token}/accept`, { method: "POST" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setSuccess(true);
            setTimeout(() => navigate(`/league/${data.leagueId}`), 2500);
        } catch (err) {
            setActionError(err.message || "Misslyckades att koppla konto.");
        } finally {
            setAccepting(false);
        }
    };

    const handleLogin = async () => {
        setActionError(null);
        try {
            if (isIOS) {
                await signInWithRedirect(auth, googleProvider);
            } else {
                await signInWithPopup(auth, googleProvider);
                await callAccept();
            }
        } catch (err) {
            if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
                setActionError("Inloggning misslyckades. Försök igen.");
            }
        }
    };

    if (loading) {
        return (
            <div className="invite-page">
                <div className="invite-card">
                    <p className="invite-loading">Laddar inbjudan...</p>
                </div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="invite-page">
                <div className="invite-card">
                    <h2 className="invite-title">Inbjudan</h2>
                    <p className="invite-error">{pageError}</p>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="invite-page">
                <div className="invite-card">
                    <h2 className="invite-title">Klart!</h2>
                    <p className="invite-success">Ditt konto är nu kopplat. Du omdirigeras...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="invite-page">
            <div className="invite-card">
                <h1 className="invite-app-name">Svärmorsdrömmar</h1>
                <hr className="invite-divider" />
                <h2 className="invite-title">Du har blivit inbjuden</h2>
                <p className="invite-player-name">{invite?.playerName}</p>
                <p className="invite-description">
                    Logga in med Google för att koppla ditt konto till din spelarprofil.
                </p>

                {user ? (
                    <div className="invite-signed-in">
                        <p className="invite-signed-in-text">Inloggad som <strong>{user.displayName || user.email}</strong></p>
                        <button
                            className="invite-accept-btn"
                            onClick={callAccept}
                            disabled={accepting}
                        >
                            {accepting ? "Kopplar konto..." : "Koppla konto"}
                        </button>
                    </div>
                ) : (
                    <button
                        className="invite-google-btn"
                        onClick={handleLogin}
                        disabled={accepting}
                    >
                        <GoogleIcon />
                        {accepting ? "Loggar in..." : "Fortsätt med Google"}
                    </button>
                )}

                {actionError && <p className="invite-error">{actionError}</p>}
            </div>
        </div>
    );
}
